import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import StudentLayout from '../components/StudentLayout';
import Loader from '../components/Loader';
import AuthService from '../services/authService';
import axiosInstance from '../utils/axiosInstance';

const Wrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: 768px) {
    gap: 16px;
  }
`;

const Section = styled.section`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 14px;
    border-radius: 14px;
  }
`;

const SectionHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 22px;
  color: #111827;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const Subtitle = styled.p`
  margin: 0;
  color: #6b7280;
  font-size: 14px;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
    gap: 8px;
  }
`;

const StatCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 12px 14px;
  background: #f9fafb;

  @media (max-width: 768px) {
    padding: 10px 12px;
  }
`;

const StatLabel = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #9ca3af;

  @media (max-width: 768px) {
    font-size: 10.5px;
  }
`;

const StatValue = styled.div`
  margin-top: 4px;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const SubjectsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 768px) {
    gap: 12px;
  }
`;

const SubjectCard = styled.div`
  border: 1px solid ${(p) => (p.$overdue ? '#fcd34d' : '#e5e7eb')};
  border-radius: 16px;
  padding: 18px;
  background: ${(p) =>
    p.$overdue ? 'linear-gradient(135deg, #fefce8, #fef9c3)' : 'linear-gradient(135deg, #ffffff, #fbfbff)'};
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.04);

  @media (max-width: 768px) {
    padding: 14px;
    border-radius: 14px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const SubjectTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const SubjectMeta = styled.div`
  font-size: 13px;
  color: #6b7280;
  margin-top: 2px;
`;

const SubjectStatusLine = styled.div`
  margin-top: 8px;
  font-size: 14px;
  color: #0f172a;
`;

const SubjectStatusEmphasis = styled.span`
  font-weight: 600;
  color: #1d4ed8;
`;

const StatusPill = styled.span`
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${(p) => p.$bg || '#e5e7eb'};
  color: ${(p) => p.$color || '#374151'};
  flex-shrink: 0;

  @media (max-width: 768px) {
    align-self: flex-start;
  }
`;

const StatusPillGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    align-items: flex-start;
    flex-direction: row;
    flex-wrap: wrap;
  }
`;

const DetailGrid = styled.div`
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;

  @media (max-width: 768px) {
    margin-top: 12px;
    display: flex;
    flex-direction: column;
    gap: 0;
    border-top: 1px solid #f1f5f9;
  }
`;

const DetailItem = styled.div`
  border: 1px dashed #e5e7eb;
  border-radius: 12px;
  padding: 10px 12px;
  background: #fff;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    border: none;
    border-bottom: 1px solid #f1f5f9;
    border-radius: 0;
    padding: 10px 2px;
    background: transparent;
  }
`;

const MarkDetailItem = styled(DetailItem)`
  border-style: solid;
  border-color: ${(p) => (p.$isWarning ? '#ea580c' : '#4338ca')};
  background: ${(p) => (p.$isWarning ? '#fff7ed' : '#eef2ff')};

  @media (max-width: 768px) {
    flex-wrap: wrap;
    border-style: none;
    border-bottom: 1px solid #f1f5f9;
    border-left: 3px solid ${(p) => (p.$isWarning ? '#ea580c' : '#4338ca')};
    background: transparent;
    padding-left: 10px;
  }
`;

const DetailLabel = styled.div`
  font-size: 12px;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  @media (max-width: 768px) {
    text-transform: none;
    letter-spacing: normal;
    font-size: 13px;
    color: #64748b;
    flex-shrink: 0;
  }
`;

const DetailValue = styled.div`
  margin-top: 4px;
  font-size: 14px;
  color: ${(p) => (p.$overdue ? '#b45309' : '#0f172a')};
  font-weight: 600;

  @media (max-width: 768px) {
    margin-top: 0;
    text-align: right;
  }
`;

const MarkDetailValue = styled(DetailValue)`
  background: ${(p) => (p.$isWarning ? '#fef3c7' : '#eef2ff')};
  color: ${(p) => (p.$isWarning ? '#b45309' : '#4338ca')};
  padding: 4px 10px;
  border-radius: 999px;
  display: inline-block;
`;

const MarkRuleNote = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  color: ${(p) => (p.$isWarning ? '#b45309' : '#475569')};

  @media (max-width: 768px) {
    width: 100%;
    margin: 4px 0 6px;
  }
`;

const EmptyState = styled.div`
  padding: 32px;
  border: 2px dashed #dbeafe;
  border-radius: 16px;
  text-align: center;
  color: #0f172a;
  background: #eff6ff;
`;

const EmptyTitle = styled.div`
  font-weight: 700;
  font-size: 18px;
`;

const EmptyText = styled.p`
  margin: 8px 0 0;
  color: #475569;
  font-size: 14px;
`;

const ErrorState = styled(EmptyState)`
  border-color: #fee2e2;
  background: #fef2f2;
  color: #991b1b;
`;

const HoursProgressCard = styled.div`
  margin-top: 18px;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 16px;
  background: #f9fafb;
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (max-width: 768px) {
    margin-top: 14px;
    padding: 12px;
  }
`;

const HoursProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 4px;
  }
`;

const HoursProgressTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
`;

const HoursProgressSubtitle = styled.div`
  font-size: 12px;
  color: #64748b;
`;

const HoursFigure = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
`;

const HoursTrack = styled.div`
  position: relative;
  width: 100%;
  height: 10px;
  border-radius: 999px;
  background: #e2e8f0;
  overflow: hidden;
`;

const HoursFill = styled.div`
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #34d399, #10b981);
  width: ${(p) => `${Math.min(p.$value || 0, 100)}%`};
  transition: width 0.3s ease;
`;

const HoursLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const HoursChip = styled.span`
  border-radius: 999px;
  border: 1px solid ${(p) => p.$border || '#e2e8f0'};
  background: ${(p) => p.$bg || '#ffffff'};
  color: ${(p) => p.$color || '#0f172a'};
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
`;

const HoursPlaceholder = styled.div`
  border: 1px dashed #cbd5f5;
  border-radius: 12px;
  background: #ffffff;
  color: #475569;
  font-size: 13px;
  padding: 12px;
`;

const HoursBadgeRow = styled.div`
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  @media (max-width: 768px) {
    gap: 6px;
  }
`;

const HoursBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 999px;
  border: 1px solid ${(p) => p.$border || '#e5e7eb'};
  background: ${(p) => p.$bg || '#f8fafc'};
  color: ${(p) => p.$color || '#0f172a'};
  font-size: 12px;
  font-weight: 600;
  padding: 4px 12px;

  @media (max-width: 768px) {
    font-size: 11px;
    padding: 4px 10px;
  }
`;

const ToggleDetailsButton = styled.button`
  margin-top: 12px;
  border: none;
  background: transparent;
  color: #4338ca;
  font-size: 13px;
  font-weight: 600;
  padding: 4px 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  &:hover {
    text-decoration: underline;
  }
`;

const ToggleChevron = styled.span`
  display: inline-block;
  transition: transform 0.2s ease;
  transform: rotate(${(p) => (p.$expanded ? '180deg' : '0deg')});
`;

const ViewToggle = styled.div`
  display: inline-flex;
  background: #f1f5f9;
  border-radius: 10px;
  padding: 4px;
  gap: 2px;
  margin-bottom: 16px;
`;

const ViewToggleButton = styled.button`
  border: none;
  border-radius: 8px;
  padding: 7px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  background: ${(p) => (p.$active ? '#4338ca' : 'transparent')};
  color: ${(p) => (p.$active ? '#ffffff' : '#475569')};
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: ${(p) => (p.$active ? '#4338ca' : '#e2e8f0')};
  }
`;

const SummaryTableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
`;

const SummaryTable = styled.table`
  width: 100%;
  min-width: 740px;
  border-collapse: collapse;
  font-size: 13px;

  thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    text-align: left;
    background: #f8fafc;
    color: #64748b;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 10px 14px;
    border-bottom: 1px solid #e5e7eb;
    white-space: nowrap;
  }

  tbody td {
    padding: 12px 14px;
    border-bottom: 1px solid #f1f5f9;
    color: #0f172a;
    vertical-align: middle;
    white-space: nowrap;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:hover {
    background: #f8fafc;
  }
`;

const SummarySubjectName = styled.div`
  font-weight: 600;
  font-size: 13.5px;
  color: #0f172a;
  white-space: normal;
  max-width: 260px;
`;

const SummarySubjectMeta = styled.div`
  margin-top: 2px;
  font-size: 11.5px;
  color: #94a3b8;
  white-space: normal;
`;

const SummaryActualDate = styled.div`
  margin-top: 2px;
  font-size: 11.5px;
  color: ${(p) => (p.$overdue ? '#b45309' : '#94a3b8')};
  font-weight: ${(p) => (p.$overdue ? 600 : 400)};
  white-space: normal;
`;

const SummaryRow = styled.tr`
  background: ${(p) => (p.$overdue ? '#fef9c3' : 'transparent')};

  &:hover {
    background: ${(p) => (p.$overdue ? '#fef08a' : '#f8fafc')};
  }
`;

const SummaryMarkValue = styled.span`
  font-weight: 700;
  color: ${(p) => (p.$isWarning ? '#b45309' : '#4338ca')};
`;

const SummaryFeedbackButton = styled.button`
  border: 1px solid #c7d2fe;
  background: #eef2ff;
  color: #4338ca;
  border-radius: 999px;
  min-width: 30px;
  height: 26px;
  padding: 0 10px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: #e0e7ff;
  }
`;

const SemesterRow = styled.tr`
  background: #f8fafc;
`;

const SemesterRowCell = styled.td`
  padding: 10px 14px;
  font-size: 11px;
  font-weight: 700;
  color: #4338ca;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
`;

const SemesterDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: ${(p) => (p.$first ? '0 0 12px' : '24px 0 12px')};

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e5e7eb;
  }
`;

const SemesterDividerLabel = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: #4338ca;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  white-space: nowrap;
`;

const ButtonRow = styled.div`
  margin-top: 18px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;

  @media (max-width: 768px) {
    margin-top: 14px;
  }
`;

const FeedbackButton = styled.button`
  border: 1px solid #818cf8;
  background: #eef2ff;
  color: #312e81;
  border-radius: 999px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;
  &:hover {
    background: #e0e7ff;
    transform: translateY(-1px);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 11px 16px;
  }
`;

const FeedbackCountBubble = styled.span`
  min-width: 24px;
  height: 24px;
  border-radius: 999px;
  background: #4338ca;
  color: #ffffff;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.65);
  z-index: 1200;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px 16px;

  @media (max-width: 768px) {
    align-items: flex-end;
    padding: 0;
  }
`;

const ModalCard = styled.div`
  width: min(720px, 100%);
  background: #ffffff;
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 30px 80px rgba(15, 23, 42, 0.25);
  max-height: calc(100vh - 48px);
  overflow-y: auto;

  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 999px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }

  @media (max-width: 768px) {
    width: 100%;
    max-height: 88vh;
    border-radius: 20px 20px 0 0;
    padding: 18px 16px calc(18px + env(safe-area-inset-bottom));
    animation: slideUp 0.25s ease;
  }

  @keyframes slideUp {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
  }
`;

const ModalGrabber = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
    width: 40px;
    height: 4px;
    border-radius: 999px;
    background: #e2e8f0;
    margin: 0 auto 12px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;

  @media (max-width: 768px) {
    position: sticky;
    top: 0;
    background: #ffffff;
    padding-top: 2px;
    margin: -2px 0 12px;
    z-index: 1;
  }
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  color: #0f172a;
`;

const ModalSubtitle = styled.p`
  margin: 4px 0 0;
  color: #475569;
  font-size: 14px;
`;

const ModalCloseButton = styled.button`
  border: none;
  background: #f1f5f9;
  color: #0f172a;
  font-weight: 600;
  border-radius: 999px;
  padding: 6px 16px;
  cursor: pointer;
  transition: background 0.2s ease;
  &:hover {
    background: #e2e8f0;
  }
`;

const ModalError = styled.div`
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 12px;
  font-size: 14px;
`;

const FeedbackList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FeedbackItem = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 16px;
  background: #f8fafc;
`;

const FeedbackItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 2px;
  }
`;

const FeedbackDate = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
`;

const FeedbackMeta = styled.div`
  font-size: 12px;
  color: #475569;
`;

const FeedbackAuthor = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  text-align: right;

  @media (max-width: 768px) {
    text-align: left;
    color: #4338ca;
  }
`;

const FeedbackBody = styled.p`
  margin: 0;
  font-size: 14px;
  color: #1e293b;
  line-height: 1.5;
  white-space: pre-wrap;
`;

const FeedbackFooter = styled.div`
  margin-top: 10px;
  font-size: 12px;
  color: #64748b;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const FeedbackEmpty = styled.div`
  padding: 24px;
  border: 1px dashed #dbeafe;
  border-radius: 16px;
  background: #f8fafc;
  text-align: center;
  color: #475569;
`;

const statusPalette = {
  'not submitted': { bg: '#fee2e2', color: '#b91c1c', label: 'Not submitted' },
  'on-time submission': { bg: '#dcfce7', color: '#166534', label: 'On-time' },
  'late submission': { bg: '#fef3c7', color: '#b45309', label: 'Late submission' },
  're-submission': { bg: '#ede9fe', color: '#5b21b6', label: 'Re-submission' },
  submitted: { bg: '#dcfce7', color: '#166534', label: 'Submitted' },
};

const FORMAL_STATUS_COURSES = [
  'BEng (Hons) Computer Networking',
  'BA (Hons) in Business Administration',
  'BEng (Hons) in Software Engineering',
];

const FALLBACK_STATUS_PILL = { bg: '#e5e7eb', color: '#374151', label: 'Status' };

// Course-progress status (batch_subjects.status: 'not started' | 'ongoing' | 'completed'),
// distinct from the assignment submission status above.
const courseStatusPalette = {
  completed: { bg: '#dcfce7', color: '#166534', label: 'Completed' },
  ongoing: { bg: '#dbeafe', color: '#1d4ed8', label: 'Ongoing' },
  'not started': { bg: '#e5e7eb', color: '#475569', label: 'Not started' },
};

const getCourseStatusDisplay = (status) => {
  const key = typeof status === 'string' ? status.trim().toLowerCase() : '';
  if (courseStatusPalette[key]) return courseStatusPalette[key];
  return { bg: '#e5e7eb', color: '#475569', label: status || 'Not set' };
};

const formatDate = (value) => {
  if (!value) return 'Not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not set';
  return date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatNumber = (value) => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'number') {
    return Number.isInteger(value) ? value.toString() : value.toFixed(1);
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? String(value) : parsed.toString();
};

const formatTime = (value) => {
  if (!value) return null;
  const [hour, minute] = String(value).split(':');
  if (!hour || !minute) return value;
  return `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
};

const formatDateTime = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDuration = (minutes) => {
  if (minutes === null || minutes === undefined) return null;
  const parsed = Number(minutes);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return `${parsed} min${parsed === 1 ? '' : 's'}`;
};

const getTimeWindowLabel = (start, end) => {
  const startLabel = formatTime(start);
  const endLabel = formatTime(end);
  if (startLabel && endLabel) {
    return `${startLabel} - ${endLabel}`;
  }
  return startLabel || endLabel || null;
};
const getSemesterLabel = (semester) =>
  semester === null || semester === undefined || semester === '' ? 'Semester N/A' : `Semester ${semester}`;

const LECTURE_HOURS_PER_CLASS = 2;

const toFiniteNumber = (value) => {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatHours = (value) => {
  if (value === null || value === undefined) return '—';
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '—';
  const rounded = Math.round(parsed * 10) / 10;
  const formatted = Number.isInteger(rounded) ? `${Math.trunc(rounded)}` : rounded.toFixed(1);
  const label = Math.abs(rounded - 1) < 0.05 ? 'hr' : 'hrs';
  return `${formatted} ${label}`;
};

const formatClassCount = (value) => {
  if (value === null || value === undefined) return '—';
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '—';
  const rounded = Math.round(parsed * 10) / 10;
  const formatted = Number.isInteger(rounded) ? `${Math.trunc(rounded)}` : rounded.toFixed(1);
  const label = Math.abs(rounded - 1) < 0.05 ? 'class' : 'classes';
  return `${formatted} ${label}`;
};

const formatCompactNumber = (value) => {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  const rounded = Math.round(parsed * 10) / 10;
  return Number.isInteger(rounded) ? `${Math.trunc(rounded)}` : rounded.toFixed(1);
};

const formatHoursCompact = (value) => {
  const formatted = formatCompactNumber(value);
  if (formatted === null) return '—';
  return `${formatted}h`;
};

const formatClassCompact = (value) => {
  const formatted = formatCompactNumber(value);
  return formatted ?? '—';
};

const normalizeHoursMeta = (subject) => {
  const scheduledHours = toFiniteNumber(subject?.scheduledHours) ?? 0;
  const scheduledClassesExplicit = toFiniteNumber(subject?.scheduledClasses);
  const scheduledClasses =
    scheduledClassesExplicit !== null
      ? scheduledClassesExplicit
      : scheduledHours / LECTURE_HOURS_PER_CLASS;
  const expectedClasses = toFiniteNumber(subject?.expectedClasses);
  const expectedHoursFromClasses =
    expectedClasses !== null ? expectedClasses * LECTURE_HOURS_PER_CLASS : null;
  const expectedHours = toFiniteNumber(subject?.expectedHours) ?? expectedHoursFromClasses;
  const pendingHoursExplicit = toFiniteNumber(subject?.pendingHours);
  const pendingClassesExplicit = toFiniteNumber(subject?.pendingClasses);
  const hoursCoveredExplicit = toFiniteNumber(subject?.hoursCovered);
  const hoursPendingExplicit = toFiniteNumber(subject?.hoursPending);
  const hoursExceededExplicit = toFiniteNumber(subject?.hoursExceeded);

  const derivedPendingHours =
    expectedHours !== null ? Math.max(expectedHours - scheduledHours, 0) : null;
  const derivedPendingClasses =
    expectedClasses !== null ? Math.max(expectedClasses - scheduledClasses, 0) : null;
  const derivedCovered =
    expectedHours !== null ? Math.min(scheduledHours, expectedHours) : scheduledHours;
  const derivedExceeded =
    expectedHours !== null ? Math.max(scheduledHours - expectedHours, 0) : null;

  const pendingHours = pendingHoursExplicit ?? derivedPendingHours;
  const pendingClasses = pendingClassesExplicit ?? derivedPendingClasses;
  const hoursPending = hoursPendingExplicit ?? pendingHours;
  const hoursCovered = hoursCoveredExplicit ?? derivedCovered;
  const hoursExceeded = hoursExceededExplicit ?? derivedExceeded;
  const coveragePercent =
    expectedHours && expectedHours > 0
      ? Math.min(100, Math.round((hoursCovered / expectedHours) * 100))
      : 0;
  const overScheduled = Boolean(
    subject?.overScheduled ?? (expectedHours !== null && scheduledHours > expectedHours)
  );

  return {
    scheduledHours,
    scheduledClasses,
    expectedHours,
    expectedClasses,
    allocatedClasses: scheduledClasses,
    pendingHours,
    pendingClasses,
    hoursCovered,
    hoursPending,
    hoursExceeded,
    coveragePercent,
    overScheduled,
  };
};

const getDisplayMark = (subject, tableSource, useSecondMark = false) => {
  const status = subject?.submissionStatus || 'not submitted';
  const normalizedStatus = normalizeStatusKey(status);
  const primaryMark = subject?.marks ?? subject?.mark ?? null;
  const secondaryMark = useSecondMark ? getSecondMarkValue(subject) : null;
  let rawMark = useSecondMark ? secondaryMark ?? primaryMark : primaryMark;

  if (tableSource === 'assignment_submission') {
    if ((rawMark === null || rawMark === undefined || rawMark === '') && normalizedStatus !== 'not submitted') {
      rawMark = 'SN';
    }
    const markValue = rawMark ?? 'NS';
    const rendered = typeof markValue === 'number' ? formatNumber(markValue) : markValue;
    const titleBits = [subject?.subjectShortName, status].filter(Boolean).join(' • ');
    return { value: rendered, title: titleBits || 'Submission status', rawValue: markValue };
  }

  const fallback = rawMark;
  const sanitizedRaw = fallback === '' ? null : fallback;
  return {
    value: sanitizedRaw !== null && sanitizedRaw !== undefined ? formatNumber(sanitizedRaw) : '—',
    title: 'Reported mark',
    rawValue: sanitizedRaw,
  };
};

const normalizeStatusKey = (status) => (typeof status === 'string' ? status.trim().toLowerCase() : '');

const getStatusLabel = (status) => {
  const key = normalizeStatusKey(status);
  if (statusPalette[key]?.label) {
    return statusPalette[key].label;
  }
  if (!status) {
    return 'Status';
  }
  return status
    .split(' ')
    .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1))
    .join(' ');
};

const getStatusDisplay = (status, forceBinary = false) => {
  const normalized = normalizeStatusKey(status) || 'not submitted';
  if (forceBinary) {
    const binaryKey = normalized === 'not submitted' ? 'not submitted' : 'submitted';
    const palette = statusPalette[binaryKey] || FALLBACK_STATUS_PILL;
    return {
      key: binaryKey,
      label: getStatusLabel(binaryKey),
      palette,
    };
  }
  const palette = statusPalette[normalized] || FALLBACK_STATUS_PILL;
  return {
    key: normalized,
    label: getStatusLabel(status || normalized),
    palette,
  };
};

const getSecondMarkValue = (subject) =>
  subject?.secondMarks ?? subject?.second_marks ?? subject?.second_mark ?? subject?.secondMark ?? null;

const getMarkAdjustmentNote = (subject, markOverride) => {
  const statusKey = normalizeStatusKey(subject?.submissionStatus);
  if (statusKey !== 'late submission' && statusKey !== 're-submission') {
    return null;
  }
  const numericMarkSource = markOverride !== undefined ? markOverride : subject?.marks;
  const numericMark = toFiniteNumber(numericMarkSource);
  if (numericMark === null) {
    return null;
  }
  let capValue = null;
  if (numericMark > 43) {
    capValue = 43;
  } else if (numericMark > 40) {
    capValue = 40;
  }
  if (capValue === null) {
    return null;
  }
  const label = getStatusLabel(subject?.submissionStatus);
  return `${label} policy: mark recorded as ${capValue}.`;
};

export default function Submissions() {
  const [registration, setRegistration] = useState(null);
  const [studentId, setStudentId] = useState(null);
  const [summary, setSummary] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [feedbackModal, setFeedbackModal] = useState({ open: false, subject: null });
  const [feedbackCache, setFeedbackCache] = useState({});
  const [feedbackLoadingId, setFeedbackLoadingId] = useState(null);
  const [feedbackError, setFeedbackError] = useState(null);
  const [expandedSubjectIds, setExpandedSubjectIds] = useState({});
  const [viewMode, setViewMode] = useState('summary');

  const toggleSubjectDetails = (subjectId) => {
    setExpandedSubjectIds((prev) => ({ ...prev, [subjectId]: !prev[subjectId] }));
  };

  useEffect(() => {
    try {
      const data = AuthService.getUser();
      setRegistration(data?.selectedRegistration || null);
      setStudentId(data?.studentId || data?.student?.studentId || null);
    } catch (err) {
      console.error('Failed to read auth payload', err);
      setError('Unable to read student information. Please sign in again.');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!studentId || !registration?.registrationNumber || !registration?.batchId) {
      if (registration && !registration.batchId) {
        setError('Selected registration is missing batch details.');
      }
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    axiosInstance
      .get(`/students/${encodeURIComponent(studentId)}/registrations/${encodeURIComponent(registration.registrationNumber)}/subject-summary`)
      .then((resp) => {
        if (cancelled) return;
        setSummary(resp?.data?.summary || null);
        setSubjects(Array.isArray(resp?.data?.subjects) ? resp.data.subjects : []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to fetch submission summary', err);
        const message = err?.response?.data?.error || err?.response?.data?.message || "We couldn't load submissions right now.";
        setError(message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [studentId, registration]);

  const fetchFeedbackForSubject = async (subjectId) => {
    if (!studentId || !subjectId) {
      setFeedbackError('Missing student or subject details.');
      return;
    }
    setFeedbackLoadingId(subjectId);
    setFeedbackError(null);
    try {
      const resp = await axiosInstance.get(
        `/students/${encodeURIComponent(studentId)}/batch-subjects/${encodeURIComponent(subjectId)}/feedback`
      );
      const entries = Array.isArray(resp?.data?.feedback) ? resp.data.feedback : [];
      setFeedbackCache((prev) => ({
        ...prev,
        [subjectId]: entries,
      }));
    } catch (err) {
      console.error('Failed to load lecturer feedback', err);
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        'Unable to load lecturer feedback right now.';
      setFeedbackError(message);
    } finally {
      setFeedbackLoadingId(null);
    }
  };

  const handleOpenFeedback = (subject) => {
    if (!subject) {
      return;
    }
    const subjectId = subject.batchSubjectId;
    setFeedbackModal({ open: true, subject });
    if (!subjectId) {
      setFeedbackError('Batch subject reference is missing for this entry.');
      return;
    }
    if (!feedbackCache[subjectId]) {
      fetchFeedbackForSubject(subjectId);
    } else {
      setFeedbackError(null);
    }
  };

  const handleCloseFeedback = () => {
    setFeedbackModal({ open: false, subject: null });
    setFeedbackError(null);
  };

  const courseName = registration?.courseName || '';
  const title = courseName || registration?.courseShortName || 'Submissions';
  const requiresFormalSubmissionStatus = useMemo(() => {
    if (!courseName) {
      return false;
    }
    const normalizedCourse = courseName.toLowerCase();
    return FORMAL_STATUS_COURSES.some((target) => normalizedCourse.includes(target.toLowerCase()));
  }, [courseName]);
  const useBinaryStatuses = !requiresFormalSubmissionStatus;
  const tableSource = summary?.source || 'unknown';
  const modalSubjectId = feedbackModal.subject?.batchSubjectId;
  const modalEntries = modalSubjectId ? feedbackCache[modalSubjectId] : null;
  const isFeedbackLoading = Boolean(modalSubjectId && feedbackLoadingId === modalSubjectId);

  // Derives everything both the Summary table and the Detail cards need from
  // a subject, then orders them by semester (ascending) and, within each
  // semester, by lecturing-hours coverage from highest to lowest -- so the
  // most complete subjects surface first within their term.
  const enrichedSubjects = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const enriched = subjects.map((subject) => {
      const submissionStatusRaw = subject.submissionStatus || 'Not submitted';
      const statusDisplay = getStatusDisplay(submissionStatusRaw, useBinaryStatuses);
      const hoursMeta = normalizeHoursMeta(subject);
      const isReSubmission = statusDisplay.key === 're-submission';
      const useSecondMarkForSubject = requiresFormalSubmissionStatus && isReSubmission;
      const markInfo = getDisplayMark(subject, tableSource, useSecondMarkForSubject);
      const markAdjustmentNote = getMarkAdjustmentNote(subject, markInfo.rawValue);
      const isMarkWarning = Boolean(markAdjustmentNote);
      const courseStatusDisplay = getCourseStatusDisplay(subject.status);
      const actualSubmissionDate = subject.actualAssignmentSubmissionDate
        ? new Date(subject.actualAssignmentSubmissionDate)
        : null;
      const isOverdueActualSubmission = Boolean(
        actualSubmissionDate && !Number.isNaN(actualSubmissionDate.getTime()) && actualSubmissionDate < today
      );
      return {
        subject,
        statusDisplay,
        hoursMeta,
        markInfo,
        markAdjustmentNote,
        isMarkWarning,
        courseStatusDisplay,
        isOverdueActualSubmission,
      };
    });

    return enriched.sort((a, b) => {
      const aSem = Number.isFinite(Number(a.subject?.semester)) ? Number(a.subject.semester) : Number.MAX_SAFE_INTEGER;
      const bSem = Number.isFinite(Number(b.subject?.semester)) ? Number(b.subject.semester) : Number.MAX_SAFE_INTEGER;
      if (aSem !== bSem) return aSem - bSem;

      const aCoverage = a.hoursMeta.coveragePercent || 0;
      const bCoverage = b.hoursMeta.coveragePercent || 0;
      if (bCoverage !== aCoverage) return bCoverage - aCoverage;

      return (a.subject.subjectName || '').localeCompare(b.subject.subjectName || '');
    });
  }, [subjects, useBinaryStatuses, requiresFormalSubmissionStatus, tableSource]);

  const statusCounts = useMemo(() => {
    return subjects.reduce(
      (acc, subject) => {
        const key = (subject.submissionStatus || 'not submitted').toLowerCase();
        acc.total += 1;
        acc.counts[key] = (acc.counts[key] || 0) + 1;
        return acc;
      },
      { total: 0, counts: {} }
    );
  }, [subjects]);

  const courseLabel = courseName || registration?.courseShortName;
  const batchLabel = registration?.batchName || registration?.batchInternalName;

  return (
    <StudentLayout title={title}>
      <Wrapper>
        <Section>
          <SectionHeading>
            <Title>Submission overview</Title>
            <Subtitle>
              Tracking assignment progress for {courseLabel || 'your course'} · Batch {batchLabel || 'N/A'}
            </Subtitle>
          </SectionHeading>

          <SummaryGrid>
            <StatCard>
              <StatLabel>Registration #</StatLabel>
              <StatValue>{registration?.registrationNumber || 'N/A'}</StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>Batch</StatLabel>
              <StatValue>{batchLabel || 'N/A'}</StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>Total Subjects</StatLabel>
              <StatValue>{statusCounts.total}</StatValue>
            </StatCard>
          </SummaryGrid>
        </Section>

        <Section>
          <SectionHeading>
            <Title>Subject-wise details</Title>
            <Subtitle>Review release dates, deadlines, and submission states for each subject.</Subtitle>
          </SectionHeading>

          {loading && <Loader message="Loading submission data…" />}

          {!loading && error && (
            <ErrorState>
              <EmptyTitle>Unable to load submissions</EmptyTitle>
              <EmptyText>{error}</EmptyText>
            </ErrorState>
          )}

          {!loading && !error && enrichedSubjects.length === 0 && (
            <EmptyState>
              <EmptyTitle>No submission data available</EmptyTitle>
              <EmptyText>
                Once assignments are scheduled for this batch, they will appear here with up-to-date statuses.
              </EmptyText>
            </EmptyState>
          )}

          {!loading && !error && enrichedSubjects.length > 0 && (
            <>
              <ViewToggle>
                <ViewToggleButton
                  type="button"
                  $active={viewMode === 'summary'}
                  onClick={() => setViewMode('summary')}
                >
                  Summary
                </ViewToggleButton>
                <ViewToggleButton
                  type="button"
                  $active={viewMode === 'detail'}
                  onClick={() => setViewMode('detail')}
                >
                  Detail
                </ViewToggleButton>
              </ViewToggle>

              {viewMode === 'summary' && (
                <SummaryTableWrapper>
                  <SummaryTable>
                    <thead>
                      <tr>
                        <th>Subject</th>
                        <th>Status</th>
                        <th>Progress</th>
                        <th>Marks</th>
                        <th>Coverage</th>
                        <th>Pending</th>
                        <th>Feedback</th>
                      </tr>
                    </thead>
                    <tbody>
                      {enrichedSubjects.map(({ subject, statusDisplay, hoursMeta, markInfo, isMarkWarning, courseStatusDisplay, isOverdueActualSubmission }, index) => {
                        const isNewSemesterGroup =
                          index === 0 || enrichedSubjects[index - 1].subject.semester !== subject.semester;
                        return (
                        <React.Fragment key={subject.batchSubjectId}>
                          {isNewSemesterGroup && (
                            <SemesterRow>
                              <SemesterRowCell colSpan={7}>{getSemesterLabel(subject.semester)}</SemesterRowCell>
                            </SemesterRow>
                          )}
                          <SummaryRow $overdue={isOverdueActualSubmission}>
                          <td>
                            <SummarySubjectName>{subject.subjectName}</SummarySubjectName>
                            <SummarySubjectMeta>
                              {subject.subjectShortName ? `${subject.subjectShortName} · ` : ''}
                              Sem {subject.semester || 'N/A'}
                            </SummarySubjectMeta>
                            <SummaryActualDate $overdue={isOverdueActualSubmission}>
                              Actual submission: {formatDate(subject.actualAssignmentSubmissionDate)}
                            </SummaryActualDate>
                          </td>
                          <td>
                            <StatusPill $bg={statusDisplay.palette.bg} $color={statusDisplay.palette.color}>
                              {statusDisplay.label}
                            </StatusPill>
                          </td>
                          <td>
                            <StatusPill $bg={courseStatusDisplay.bg} $color={courseStatusDisplay.color}>
                              {courseStatusDisplay.label}
                            </StatusPill>
                          </td>
                          <td>
                            <SummaryMarkValue $isWarning={isMarkWarning} title={markInfo.title}>
                              {markInfo.value}
                            </SummaryMarkValue>
                          </td>
                          <td>{hoursMeta.expectedHours ? `${hoursMeta.coveragePercent}%` : '—'}</td>
                          <td>
                            {hoursMeta.pendingHours !== null && hoursMeta.pendingHours > 0
                              ? formatHoursCompact(hoursMeta.pendingHours)
                              : '—'}
                          </td>
                          <td>
                            <SummaryFeedbackButton type="button" onClick={() => handleOpenFeedback(subject)}>
                              {subject.feedbackCount || 0}
                            </SummaryFeedbackButton>
                          </td>
                          </SummaryRow>
                        </React.Fragment>
                        );
                      })}
                    </tbody>
                  </SummaryTable>
                </SummaryTableWrapper>
              )}

              {viewMode === 'detail' && (
            <SubjectsGrid>
              {enrichedSubjects.map(({ subject, statusDisplay, hoursMeta, markInfo, markAdjustmentNote, isMarkWarning, courseStatusDisplay, isOverdueActualSubmission }, index) => {
                const palette = statusDisplay.palette;
                const statusLabel = statusDisplay.label;
                const formalStatusText = statusLabel;
                const isExpanded = Boolean(expandedSubjectIds[subject.batchSubjectId]);
                const isNewSemesterGroup =
                  index === 0 || enrichedSubjects[index - 1].subject.semester !== subject.semester;
                return (
                  <React.Fragment key={subject.batchSubjectId}>
                    {isNewSemesterGroup && (
                      <SemesterDivider $first={index === 0}>
                        <SemesterDividerLabel>{getSemesterLabel(subject.semester)}</SemesterDividerLabel>
                      </SemesterDivider>
                    )}
                    <SubjectCard $overdue={isOverdueActualSubmission}>
                    <CardHeader>
                      <div>
                        <SubjectTitle>{subject.subjectName}</SubjectTitle>
                        <SubjectMeta>
                          {subject.subjectShortName ? `${subject.subjectShortName} · ` : ''}
                          Semester {subject.semester || 'N/A'} · Batch subject ID: {subject.batchSubjectId}
                        </SubjectMeta>
                        {requiresFormalSubmissionStatus && (
                          <SubjectStatusLine>
                            Submission Status:{' '}
                            <SubjectStatusEmphasis>{formalStatusText}</SubjectStatusEmphasis>
                          </SubjectStatusLine>
                        )}
                      </div>
                      <StatusPillGroup>
                        <StatusPill $bg={palette.bg} $color={palette.color}>
                          {statusLabel}
                        </StatusPill>
                        <StatusPill $bg={courseStatusDisplay.bg} $color={courseStatusDisplay.color}>
                          {courseStatusDisplay.label}
                        </StatusPill>
                      </StatusPillGroup>
                    </CardHeader>

                    <HoursBadgeRow>
                      {hoursMeta.expectedHours !== null && (
                        <HoursBadge $bg="#cffafe" $border="#22d3ee" $color="#0f172a">
                          E : {formatHoursCompact(hoursMeta.expectedHours)}
                          <span>({formatClassCompact(hoursMeta.expectedClasses)})</span>
                        </HoursBadge>
                      )}
                      <HoursBadge
                        $bg={hoursMeta.overScheduled ? '#fde2e4' : '#ede9fe'}
                        $border={hoursMeta.overScheduled ? '#f43f5e' : '#c7d2fe'}
                        $color={hoursMeta.overScheduled ? '#991b1b' : '#312e81'}
                      >
                        S : {formatHoursCompact(hoursMeta.scheduledHours)}
                        <span>({formatClassCompact(hoursMeta.scheduledClasses)})</span>
                      </HoursBadge>
                      {hoursMeta.pendingHours !== null && hoursMeta.pendingHours > 0 && (
                        <HoursBadge $bg="#fef9c3" $border="#fcd34d" $color="#92400e">
                          P : {formatHoursCompact(hoursMeta.pendingHours)}
                          <span>({formatClassCompact(hoursMeta.pendingClasses)})</span>
                        </HoursBadge>
                      )}
                      {!isExpanded && (
                        <HoursBadge
                          $bg={isMarkWarning ? '#fef3c7' : '#eef2ff'}
                          $border={isMarkWarning ? '#fcd34d' : '#c7d2fe'}
                          $color={isMarkWarning ? '#92400e' : '#4338ca'}
                          title={markInfo.title}
                        >
                          Marks : {markInfo.value}
                        </HoursBadge>
                      )}
                      {hoursMeta.overScheduled && (
                        <HoursBadge $bg="#f5d0fe" $border="#f0abfc" $color="#86198f">
                          Over scheduled
                        </HoursBadge>
                      )}
                    </HoursBadgeRow>

                    {isExpanded && (
                      <DetailGrid>
                        <DetailItem>
                          <DetailLabel>Release date</DetailLabel>
                          <DetailValue>{formatDate(subject.assignmentReleaseDate)}</DetailValue>
                        </DetailItem>
                        <DetailItem>
                          <DetailLabel>Submission deadline</DetailLabel>
                          <DetailValue>{formatDate(subject.assignmentSubmissionDate)}</DetailValue>
                        </DetailItem>
                        <DetailItem>
                          <DetailLabel>Late submission</DetailLabel>
                          <DetailValue>{formatDate(subject.assignmentLateSubmissionDate)}</DetailValue>
                        </DetailItem>
                        <DetailItem>
                          <DetailLabel>Status</DetailLabel>
                          <DetailValue>
                            <StatusPill $bg={courseStatusDisplay.bg} $color={courseStatusDisplay.color}>
                              {courseStatusDisplay.label}
                            </StatusPill>
                          </DetailValue>
                        </DetailItem>
                        <DetailItem>
                          <DetailLabel>Start date</DetailLabel>
                          <DetailValue>{formatDate(subject.startDate)}</DetailValue>
                        </DetailItem>
                        <DetailItem>
                          <DetailLabel>End date</DetailLabel>
                          <DetailValue>{formatDate(subject.endDate)}</DetailValue>
                        </DetailItem>
                        <DetailItem>
                          <DetailLabel>Actual submission</DetailLabel>
                          <DetailValue $overdue={isOverdueActualSubmission}>
                            {formatDate(subject.actualAssignmentSubmissionDate)}
                          </DetailValue>
                        </DetailItem>
                        <DetailItem>
                          <DetailLabel>Expected classes</DetailLabel>
                          <DetailValue>{formatClassCount(hoursMeta.expectedClasses)}</DetailValue>
                        </DetailItem>
                        <DetailItem>
                          <DetailLabel>Allocated classes</DetailLabel>
                          <DetailValue>{formatClassCount(hoursMeta.allocatedClasses)}</DetailValue>
                        </DetailItem>
                        <DetailItem>
                          <DetailLabel>Expected lecturing hours</DetailLabel>
                          <DetailValue>{formatHours(hoursMeta.expectedHours)}</DetailValue>
                        </DetailItem>
                        <DetailItem>
                          <DetailLabel>Scheduled lecturing hours</DetailLabel>
                          <DetailValue>{formatHours(hoursMeta.scheduledHours)}</DetailValue>
                        </DetailItem>
                        <MarkDetailItem $isWarning={isMarkWarning}>
                          <DetailLabel>Marks</DetailLabel>
                          <MarkDetailValue $isWarning={isMarkWarning} title={markInfo.title}>
                            {markInfo.value}
                          </MarkDetailValue>
                          {markAdjustmentNote && (
                            <MarkRuleNote $isWarning={isMarkWarning}>{markAdjustmentNote}</MarkRuleNote>
                          )}
                        </MarkDetailItem>
                        <DetailItem>
                          <DetailLabel>Percentage</DetailLabel>
                          <DetailValue title="Covered lecturing hours as a percentage of expected lecturing hours">
                            {hoursMeta.expectedHours ? `${hoursMeta.coveragePercent}%` : '—'}
                          </DetailValue>
                        </DetailItem>
                        <DetailItem>
                          <DetailLabel>Lecturer feedback</DetailLabel>
                          <DetailValue>{subject.feedbackCount || 0} note(s)</DetailValue>
                        </DetailItem>
                        <DetailItem>
                          <DetailLabel>Last update</DetailLabel>
                          <DetailValue>{formatDate(subject.updatedAt)}</DetailValue>
                        </DetailItem>
                      </DetailGrid>
                    )}

                    <ToggleDetailsButton
                      type="button"
                      onClick={() => toggleSubjectDetails(subject.batchSubjectId)}
                    >
                      {isExpanded ? 'Show less' : 'Show more details'}
                      <ToggleChevron $expanded={isExpanded}>▾</ToggleChevron>
                    </ToggleDetailsButton>

                    <HoursProgressCard>
                      <HoursProgressHeader>
                        <div>
                          <HoursProgressTitle>Lecturing hours coverage</HoursProgressTitle>
                          <HoursProgressSubtitle>
                            Mapping scheduled sessions to planned workload
                          </HoursProgressSubtitle>
                        </div>
                        <HoursFigure>
                          {formatHours(hoursMeta.scheduledHours)}
                          {hoursMeta.expectedHours
                            ? ` / ${formatHours(hoursMeta.expectedHours)} planned`
                            : ' scheduled'}
                        </HoursFigure>
                      </HoursProgressHeader>
                      {hoursMeta.expectedHours ? (
                        <>
                          <HoursTrack>
                            <HoursFill $value={hoursMeta.coveragePercent} />
                          </HoursTrack>
                          <HoursLegend>
                            <HoursChip $bg="#ecfdf5" $border="#a7f3d0" $color="#065f46">
                              Covered: {formatHours(hoursMeta.hoursCovered)}
                            </HoursChip>
                            {hoursMeta.hoursPending !== null && hoursMeta.hoursPending > 0 && (
                              <HoursChip $bg="#fff7ed" $border="#fed7aa" $color="#92400e">
                                Pending: {formatHours(hoursMeta.hoursPending)}
                              </HoursChip>
                            )}
                            {hoursMeta.hoursExceeded !== null && hoursMeta.hoursExceeded > 0 && (
                              <HoursChip $bg="#fef2f2" $border="#fecaca" $color="#991b1b">
                                Exceeded by {formatHours(hoursMeta.hoursExceeded)}
                              </HoursChip>
                            )}
                            {hoursMeta.hoursPending === 0 && hoursMeta.hoursExceeded === 0 && (
                              <HoursChip $bg="#eef2ff" $border="#c7d2fe" $color="#312e81">
                                Target met
                              </HoursChip>
                            )}
                          </HoursLegend>
                        </>
                      ) : (
                        <HoursPlaceholder>
                          Expected classes have not been published for this subject yet.
                        </HoursPlaceholder>
                      )}
                    </HoursProgressCard>

                    <ButtonRow>
                      <FeedbackButton type="button" onClick={() => handleOpenFeedback(subject)}>
                        View lecturer feedback
                        <FeedbackCountBubble>{subject.feedbackCount || 0}</FeedbackCountBubble>
                      </FeedbackButton>
                    </ButtonRow>
                    </SubjectCard>
                  </React.Fragment>
                );
              })}
            </SubjectsGrid>
              )}
            </>
          )}
        </Section>
      </Wrapper>

      {feedbackModal.open && (
        <ModalOverlay role="presentation">
          <ModalCard role="dialog" aria-modal="true">
            <ModalGrabber />
            <ModalHeader>
              <div>
                <ModalTitle>Lecturer feedback</ModalTitle>
                <ModalSubtitle>
                  {(feedbackModal.subject?.subjectName || 'Subject') +
                    (feedbackModal.subject?.batchSubjectId ? ` · ID ${feedbackModal.subject.batchSubjectId}` : '')}
                </ModalSubtitle>
              </div>
              <ModalCloseButton type="button" onClick={handleCloseFeedback}>
                Close
              </ModalCloseButton>
            </ModalHeader>

            {feedbackError && <ModalError>{feedbackError}</ModalError>}
            {isFeedbackLoading && <Loader message="Fetching feedback entries…" />}
            {!isFeedbackLoading && !feedbackError && (!modalEntries || modalEntries.length === 0) && (
              <FeedbackEmpty>No lecturer feedback has been recorded yet.</FeedbackEmpty>
            )}
            {!isFeedbackLoading && !feedbackError && modalEntries && modalEntries.length > 0 && (
              <FeedbackList>
                {modalEntries.map((entry) => {
                  const feedbackSnippet = String(entry.feedback || 'entry').slice(0, 16);
                  const key = entry.id || `${entry.feedbackDate || 'unknown'}-${entry.createdAt || feedbackSnippet}`;
                  const timeLabel = getTimeWindowLabel(entry.startTime, entry.endTime);
                  const recordedLabel = formatDateTime(entry.createdAt);
                  const durationLabel = formatDuration(entry.durationMinutes);
                  return (
                    <FeedbackItem key={key}>
                      <FeedbackItemHeader>
                        <div>
                          <FeedbackDate>{formatDate(entry.feedbackDate)}</FeedbackDate>
                          {timeLabel && <FeedbackMeta>{timeLabel}</FeedbackMeta>}
                        </div>
                        <FeedbackAuthor>{entry.staffName || 'Lecturer'}</FeedbackAuthor>
                      </FeedbackItemHeader>
                      <FeedbackBody>{entry.feedback || 'No notes provided.'}</FeedbackBody>
                      <FeedbackFooter>
                        {durationLabel && <span>Duration: {durationLabel}</span>}
                        {recordedLabel && <span>Recorded {recordedLabel}</span>}
                      </FeedbackFooter>
                    </FeedbackItem>
                  );
                })}
              </FeedbackList>
            )}
          </ModalCard>
        </ModalOverlay>
      )}
    </StudentLayout>
  );
}
